(() => {
  'use strict';
  if (window.AUNoirNativeLinkedProductsInstalled) return;
  window.AUNoirNativeLinkedProductsInstalled = true;

  const SWATCH = '[data-au-native-swatch]';
  const states = new WeakMap();
  const responses = new Map();
  const modalRequests = new WeakMap();
  const modalOwners = new WeakMap();
  const dismissedAt = new WeakMap();
  const imagePreloads = new Map();
  let lastInput = 'pointer';
  let pointerPosition = { x: 0, y: 0 };
  let pdpRequest = 0;
  const TTL = 120000;
  let sequence = 0;

  function productUrl(value) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid linked product');
    const root = new URL(window.Shopify?.routes?.root || '/', location.origin).pathname.replace(/\/?$/, '/');
    url.pathname = root + 'products/' + url.pathname.split('/products/')[1].split('/')[0];
    url.search = '';
    url.hash = '';
    return url.pathname;
  }

  function stateFor(card) {
    let state = states.get(card);
    if (!state) {
      const url = productUrl(card.dataset.auProductUrl || card.querySelector('.product-title').href);
      state = { selected: url, shown: url, request: 0, key: 'au-linked-' + (++sequence), snapshots: new Map(), hover: null, wishlist: new Map() };
      state.snapshots.set(url, { html: card.outerHTML, time: Date.now() });
      states.set(card, state);
      card.dataset.auCardKey = state.key;
    }
    return state;
  }

  async function requestHtml(url, cardView = false, context = '') {
    const target = new URL(url, location.origin);
    if (cardView) target.searchParams.set('view', 'au-native-card');
    const key = context + '|' + target.href;
    const cached = responses.get(key);
    if (cached && Date.now() - cached.time < TTL) return cached.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    const entry = { time: Date.now() };
    const promise = fetch(target, { credentials: 'same-origin', signal: controller.signal }).then(async response => {
      if (!response.ok) throw new Error('Linked product request failed');
      return response.text();
    }).catch(error => {
      if (responses.get(key) === entry) responses.delete(key);
      throw error;
    }).finally(() => clearTimeout(timeout));
    entry.promise = promise;
    responses.set(key, entry);
    if (responses.size > 80) responses.delete(responses.keys().next().value);
    return promise;
  }

  async function cardHtml(card, url) {
    const state = stateFor(card);
    const cached = state.snapshots.get(url);
    if (cached && Date.now() - cached.time < TTL) return cached.html;
    const html = await requestHtml(url, true, card.dataset.auCountry + '|' + card.dataset.auCurrency + '|' + document.documentElement.lang);
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('product-card');
    if (!parsed || productUrl(parsed.dataset.auProductUrl) !== url || parsed.dataset.auCurrency !== card.dataset.auCurrency || parsed.dataset.auCountry !== card.dataset.auCountry) {
      throw new Error('Linked product context did not match');
    }
    state.snapshots.set(url, { html: parsed.outerHTML, time: Date.now() });
    return parsed.outerHTML;
  }

  function modalFor(card) {
    const id = card.querySelector('.product-card__quick-buy [aria-controls]')?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function ownerFor(modal) {
    const known = modalOwners.get(modal);
    if (known?.isConnected && modalFor(known) === modal) return known;
    const owner = [...document.querySelectorAll('product-card')].find(card => modalFor(card) === modal);
    if (owner) modalOwners.set(modal, owner);
    return owner;
  }

  function contextKey(data) {
    return data.country + '|' + data.currency + '|' + data.locale;
  }

  function matchesContext(data, current, url) {
    return data && current && productUrl(data.url) === url && contextKey(data) === contextKey(current);
  }

  function preloadImage(source, sizes) {
    if (!source) return Promise.resolve();
    const imageSizes = sizes || source.sizes || '100vw';
    const key = source.src + '|' + source.srcset + '|' + imageSizes;
    const cached = imagePreloads.get(key);
    if (cached && Date.now() - cached.time < TTL) return cached.promise;
    const preview = new Image();
    preview.sizes = imageSizes;
    preview.srcset = source.srcset;
    preview.src = source.src;
    const promise = preview.decode().catch(() => {});
    imagePreloads.set(key, { promise, time: Date.now() });
    if (imagePreloads.size > 40) imagePreloads.delete(imagePreloads.keys().next().value);
    return promise;
  }

  function setBusy(card, busy) {
    card.toggleAttribute('data-au-switching', busy);
    if (busy) card.setAttribute('aria-busy', 'true');
    else card.removeAttribute('aria-busy');
    card.querySelectorAll('.product-card__quick-buy, .boss-quick-size').forEach(node => { node.inert = busy; });
  }

  function setActive(card, url) {
    card.querySelectorAll('[data-au-context="card"] ' + SWATCH).forEach(swatch => {
      const active = productUrl(swatch.href) === url;
      swatch.classList.toggle('is-active', active);
      if (active) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });
  }

  function announceError(scope, url) {
    const group = scope.querySelector('[data-au-native-group-swatches]');
    const status = group?.querySelector('.au-native-swatch-status');
    if (!status) return;
    const link = document.createElement('a');
    link.href = url;
    link.textContent = group.dataset.auOpenProduct;
    status.replaceChildren(document.createTextNode(group.dataset.auError + ' '), link);
  }

  function rewriteIds(root, prefix) {
    const ids = new Map();
    root.querySelectorAll('[id]').forEach(node => {
      const id = node.getAttribute('id');
      const next = prefix + '-' + id;
      ids.set(id, next);
      node.setAttribute('id', next);
    });
    root.querySelectorAll('[for], [form], [aria-controls], [aria-labelledby], [observe-form]').forEach(node => {
      ['for', 'form', 'aria-controls', 'aria-labelledby', 'observe-form'].forEach(attribute => {
        const value = node.getAttribute(attribute);
        if (value && ids.has(value)) node.setAttribute(attribute, ids.get(value));
      });
    });
  }

  function applyCard(card, html, url) {
    const state = stateFor(card);
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-card');
    rewriteIds(next, state.key);
    const figure = next.querySelector('.product-card__figure');
    const summary = next.querySelector('[data-au-native-summary]');
    const existingModal = modalFor(card);
    const incomingModal = next.querySelector('quick-buy-modal');
    const existingHost = card.querySelector('.product-card__quick-buy');
    if (existingModal && incomingModal) {
      const incomingHost = incomingModal.closest('.product-card__quick-buy');
      incomingHost.replaceWith(existingHost);
      existingHost.hidden = false;
      existingModal.setAttribute('product-url', url);
      modalOwners.set(existingModal, card);
    } else if (existingModal?.parentElement === document.body && existingHost) {
      existingHost.hidden = true;
      figure.appendChild(existingHost);
    }
    const wishlist = card.querySelector('.wl-coll-btn');
    if (wishlist) {
      state.wishlist.set(state.shown, wishlist);
      let targetWishlist = state.wishlist.get(url);
      if (!targetWishlist) {
        targetWishlist = wishlist.cloneNode(true);
        targetWishlist.classList.remove('wl-active');
        targetWishlist.setAttribute('data-added', 'false');
        [...targetWishlist.attributes].filter(attribute => attribute.name.startsWith('data-wl-')).forEach(attribute => targetWishlist.removeAttribute(attribute.name));
      }
      targetWishlist.setAttribute('data-prohandle', next.getAttribute('handle'));
      targetWishlist.setAttribute('data-proid', next.dataset.auProductId);
      targetWishlist.setAttribute('data-varid', next.dataset.auVariantId);
      figure.querySelectorAll('.wl-coll-btn').forEach(node => node.remove());
      figure.appendChild(targetWishlist);
    }
    const oldImage = card.querySelector('.product-card__gallery-image');
    figure.querySelectorAll('.product-card__gallery-image').forEach((image, index) => {
      if (oldImage?.sizes) image.sizes = oldImage.sizes;
      image.loading = index === 0 ? 'eager' : 'lazy';
    });
    card.querySelector('.product-card__figure').replaceWith(figure);
    card.querySelector('[data-au-native-summary]').replaceWith(summary);
    card.setAttribute('handle', next.getAttribute('handle'));
    card.dataset.auProductId = next.dataset.auProductId;
    card.dataset.auVariantId = next.dataset.auVariantId;
    card.dataset.auProductUrl = url;
    card.querySelectorAll('.product-card__swatch-list').forEach(node => node.remove());
    setActive(card, url);
    state.shown = url;
    card.dispatchEvent(new CustomEvent('au:native-linked-product:change', { bubbles: true, detail: { productUrl: url, handle: next.getAttribute('handle'), productId: next.dataset.auProductId } }));
  }

  async function switchCard(card, value, commit) {
    const url = productUrl(value);
    const state = stateFor(card);
    const request = ++state.request;
    if (commit) {
      state.hover = null;
      state.pendingCommit = url;
    }
    if (state.shown === url) {
      if (commit) state.selected = url;
      state.pendingCommit = null;
      setBusy(card, false);
      return;
    }
    setBusy(card, true);
    card.querySelector('.au-native-swatch-status')?.replaceChildren();
    try {
      const html = await cardHtml(card, url);
      if (!card.isConnected || request !== state.request) return;
      const image = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-card__gallery-image');
      await Promise.race([preloadImage(image, card.querySelector('.product-card__gallery-image')?.sizes), new Promise(resolve => setTimeout(resolve, 100))]);
      if (!card.isConnected || request !== state.request) return;
      applyCard(card, html, url);
      if (commit) state.selected = url;
    } catch (error) {
      if (request !== state.request) return;
      if (commit) announceError(card, url);
      console.warn('[AU NOIR linked products]', error.message);
    } finally {
      if (request === state.request) {
        state.pendingCommit = null;
        setBusy(card, false);
      }
    }
  }

  async function switchModal(modal, swatch) {
    const url = productUrl(swatch.href);
    const current = productData(modal);
    const request = (modalRequests.get(modal) || 0) + 1;
    modalRequests.set(modal, request);
    if (productUrl(modal.getAttribute('product-url')) === url) {
      modal.removeAttribute('aria-busy');
      modal.querySelectorAll('product-form').forEach(node => { node.inert = false; });
      return;
    }
    modal.setAttribute('aria-busy', 'true');
    modal.querySelectorAll('product-form').forEach(node => { node.inert = true; });
    try {
      if (!current) throw new Error('Quick view product context unavailable');
      const html = await requestHtml(url, false, contextKey(current));
      if (modalRequests.get(modal) !== request || !modal.open) return;
      const template = new DOMParser().parseFromString(html, 'text/html').getElementById('quick-buy-content');
      if (!template?.content.firstElementChild) throw new Error('Quick view content unavailable');
      const data = productData(template.content);
      if (!matchesContext(data, current, url)) throw new Error('Quick view context did not match');
      const owner = ownerFor(modal);
      modal.setAttribute('product-url', url);
      modal.replaceChildren(template.content.cloneNode(true));
      refreshProductApps(modal, data);
      if (owner) await switchCard(owner, url, true);
      if (modalRequests.get(modal) !== request || !modal.open) return;
      const selected = modal.querySelector('[data-au-native-swatch][aria-current="true"]');
      selected?.focus({ preventScroll: true });
      modal.dispatchEvent(new CustomEvent('au:native-quick-view:change', { bubbles: true, detail: { productUrl: url } }));
    } catch (error) {
      if (modalRequests.get(modal) === request) announceError(modal, url);
      console.warn('[AU NOIR quick view]', error.message);
    } finally {
      if (modalRequests.get(modal) === request) {
        modal.removeAttribute('aria-busy');
        modal.querySelectorAll('product-form').forEach(node => { node.inert = false; });
      }
    }
  }

  function productData(scope) {
    const content = scope.querySelector('[data-au-native-product-data]')?.textContent;
    return content ? JSON.parse(content) : null;
  }

  function refreshAnalyticsContext(doc, data) {
    // Reuse Shopify's server-rendered metadata without rerunning pixel loaders.
    const source = [...doc.scripts].find(script => !script.src && script.textContent.includes('window.ShopifyAnalytics.meta'));
    const match = source?.textContent.match(/\bvar meta\s*=\s*(\{[\s\S]*?\})\s*;\s*(?:for\s*\(|$)/);
    if (!match || !window.ShopifyAnalytics) return;
    try {
      const meta = JSON.parse(match[1]);
      if (String(meta.product?.id) !== String(data.product.id)) return;
      window.ShopifyAnalytics.meta = Object.assign(window.ShopifyAnalytics.meta || {}, meta, { currency: data.currency });
      if (window.meta?.product) window.meta = meta;
    } catch (_) {}
  }

  function refreshProductHead(doc) {
    const selector = 'link[rel="canonical"], link[rel="alternate"][hreflang], meta[name="description"], meta[name^="twitter:"], meta[property^="og:"], meta[property^="product:"]';
    const existing = [...document.head.querySelectorAll(selector)];
    const incoming = [...doc.head.querySelectorAll(selector)].map(node => node.cloneNode(true));
    if (existing.length) existing[0].before(...incoming);
    else document.head.append(...incoming);
    existing.forEach(node => node.remove());
    const isProductSchema = node => {
      try {
        const containsProduct = value => {
          if (Array.isArray(value)) return value.some(containsProduct);
          if (!value || typeof value !== 'object') return false;
          const types = [].concat(value['@type'] || []);
          return types.some(type => ['Product', 'ProductGroup', 'BreadcrumbList'].includes(type)) || containsProduct(value['@graph']);
        };
        return containsProduct(JSON.parse(node.textContent));
      } catch (_) { return false; }
    };
    document.head.querySelectorAll('script[type="application/ld+json"]').forEach(node => { if (isProductSchema(node)) node.remove(); });
    doc.head.querySelectorAll('script[type="application/ld+json"]').forEach(node => { if (isProductSchema(node)) document.head.append(node.cloneNode(true)); });
  }

  function refreshProductApps(scope, data, isPdp = false) {
    if (!data?.product) return;
    const product = data.product;
    if (window.wishlistPlus) {
      window.wishlistPlus.wlProduct = { ...product, selected_or_first_available_variant: data.selectedVariant };
    }
    scope.querySelectorAll('.wl-btn[data-wishlist]').forEach(button => {
      button.setAttribute('data-proid', product.id);
      button.setAttribute('data-varid', data.selectedVariant.id);
    });
    const kiwi = scope.querySelector('.shopify-app-block[id*="kiwi"]');
    if (kiwi && window.ks?.loadSizing) {
      const productData = {
        product: String(product.id),
        title: product.title,
        vendor: product.vendor,
        type: product.type,
        tags: product.tags.join(','),
        collections: data.collections.join(','),
        images: product.images,
        options: data.options,
        variants: product.variants.map(({ id, title, available }) => ({ id, title, available }))
      };
      if (isPdp && window.KiwiSizing) window.KiwiSizing.data = productData;
      kiwi.replaceChildren();
      // Kiwi retains injection observers; unique hosts keep old products detached.
      kiwi.id = kiwi.id.replace(/-au-native-\d+$/, '') + '-au-native-' + (++sequence);
      window.ks.loadSizing({ productData, options: { el: kiwi, calculatorEl: kiwi, injectionOrder: 3, calculatorInjectionOrder: 3, displayMode: 1, calculatorDisplayMode: 1 } });
    }
    scope.querySelectorAll('.shopify-app-block:not([id*="kiwi"]) script:not([src])').forEach(source => {
      if (source.type && !['text/javascript', 'application/javascript', 'module'].includes(source.type)) return;
      const script = document.createElement('script');
      [...source.attributes].forEach(({ name, value }) => script.setAttribute(name, value));
      script.textContent = source.textContent;
      source.replaceWith(script);
    });
    if (typeof window.wishlistPlus?.applyDataI18n === 'function') window.wishlistPlus.applyDataI18n(scope);
    window.Shopify?.PaymentButton?.init();
    scope.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: scope.id?.replace('shopify-section-', '') } }));
    if (isPdp) {
      try {
        const viewed = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
        localStorage.setItem('theme:recently-viewed-products', JSON.stringify([product.id, ...viewed.filter(id => id !== product.id)].slice(0,50)));
      } catch (_) {}
    }
  }

  function setPdpBusy(section, busy) {
    section.toggleAttribute('data-au-switching', busy);
    if (busy) section.setAttribute('aria-busy', 'true');
    else section.removeAttribute('aria-busy');
    section.querySelectorAll('product-form, variant-picker, product-sticky-add-to-cart').forEach(node => { node.inert = busy; });
  }

  async function switchPdp(value, addHistory = true) {
    const request = ++pdpRequest;
    const section = document.querySelector('.shopify-section--main-product');
    const current = section && productData(section);
    if (!current) return;
    const url = productUrl(value);
    const incomingUrl = new URL(value, location.origin);
    const requestedVariant = addHistory ? null : incomingUrl.searchParams.get('variant');
    const currentVariant = section.querySelector('product-form input[name="id"]')?.value;
    const defaultVariant = current.product.variants.find(variant => variant.available) || current.product.variants[0];
    const restoredVariant = requestedVariant || String(defaultVariant?.id || '');
    if (url === productUrl(current.url) && (addHistory || restoredVariant === currentVariant)) {
      setPdpBusy(section, false);
      return;
    }
    setPdpBusy(section, true);
    section.querySelector('.au-native-swatch-status')?.replaceChildren();
    try {
      const initialUrl = requestedVariant ? url + '?variant=' + encodeURIComponent(requestedVariant) : url;
      let html = await requestHtml(initialUrl, false, contextKey(current));
      if (request !== pdpRequest || !section.isConnected) return;
      let doc = new DOMParser().parseFromString(html, 'text/html');
      let nextSection = doc.querySelector('.shopify-section--main-product');
      let data = nextSection && productData(nextSection);
      if (!matchesContext(data, current, url)) throw new Error('Product context did not match');
      if (requestedVariant && String(data.selectedVariant.id) !== requestedVariant) throw new Error('Selected size unavailable');
      const previousVariant = current.product.variants.find(variant => String(variant.id) === currentVariant);
      const sizePosition = current.options.findIndex(option => /^(size|taille|grandeur)$/i.test(option.name));
      const nextSizePosition = data.options.findIndex(option => /^(size|taille|grandeur)$/i.test(option.name));
      let variant = requestedVariant && data.product.variants.find(item => String(item.id) === requestedVariant);
      if (addHistory && !variant && previousVariant && sizePosition >= 0 && nextSizePosition >= 0) {
        variant = data.product.variants.find(item => item.options[nextSizePosition] === previousVariant.options[sizePosition]);
      }
      if (variant && variant.id !== data.selectedVariant.id) {
        html = await requestHtml(url + '?variant=' + variant.id, false, contextKey(current));
        if (request !== pdpRequest || !section.isConnected) return;
        doc = new DOMParser().parseFromString(html, 'text/html');
        nextSection = doc.querySelector('.shopify-section--main-product');
        data = nextSection && productData(nextSection);
        if (!matchesContext(data, current, url) || String(data.selectedVariant.id) !== String(variant.id)) throw new Error('Selected size unavailable');
      }
      const firstImage = nextSection.querySelector('.product-gallery__media img');
      if (firstImage) {
        await Promise.race([preloadImage(firstImage), new Promise(resolve => setTimeout(resolve, 150))]);
      }
      if (request !== pdpRequest || !section.isConnected || (!addHistory && location.href !== incomingUrl.href)) return;
      const oldTop = section.querySelector('[data-au-native-group-swatches]')?.getBoundingClientRect().top;
      const target = new URL(location.href);
      target.pathname = url;
      target.searchParams.delete('variant');
      target.searchParams.delete('option_values');
      target.searchParams.delete('view');
      target.searchParams.delete('section_id');
      target.searchParams.set('variant', data.selectedVariant.id);
      target.hash = '';
      if (addHistory) history.pushState({ ...history.state, auNativeProduct: data.product.id }, '', target);
      document.title = doc.title;
      refreshProductHead(doc);
      refreshAnalyticsContext(doc, data);
      section.replaceChildren(...nextSection.childNodes);
      section.dataset.auNativeSwitches = String(Number(section.dataset.auNativeSwitches || 0) + 1);
      setPdpBusy(section, false);
      const newTop = section.querySelector('[data-au-native-group-swatches]')?.getBoundingClientRect().top;
      if (Number.isFinite(oldTop) && Number.isFinite(newTop)) window.scrollBy({ top: newTop - oldTop, behavior: 'instant' });
      refreshProductApps(section, data, true);
      section.querySelector('[data-au-native-swatch][aria-current="true"]')?.focus({ preventScroll: true });
      section.dispatchEvent(new CustomEvent('au:native-product:change', { bubbles: true, detail: { productUrl: url, productId: data.product.id, variant: data.selectedVariant } }));
    } catch (error) {
      if (request === pdpRequest) {
        if (!addHistory) location.assign(incomingUrl.href);
        else announceError(section, url);
      }
      console.warn('[AU NOIR product page]', error.message);
    } finally {
      if (request === pdpRequest) setPdpBusy(section, false);
    }
  }

  const pdpPreloads = new Map();

  async function preloadPdp(swatch) {
    const section = swatch.closest('.shopify-section--main-product');
    const current = section && productData(section);
    if (!current || navigator.connection?.saveData) return;
    const url = productUrl(swatch.href);
    if (url === productUrl(current.url)) return;
    const currentVariant = section.querySelector('product-form input[name="id"]')?.value;
    const context = contextKey(current);
    const key = context + '|' + url + '|' + currentVariant;
    const cached = pdpPreloads.get(key);
    if (cached && Date.now() - cached < TTL) return;
    pdpPreloads.set(key, Date.now());
    if (pdpPreloads.size > 40) pdpPreloads.delete(pdpPreloads.keys().next().value);
    try {
      let html = await requestHtml(url, false, context);
      let next = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section--main-product');
      const data = next && productData(next);
      if (!data || productUrl(data.url) !== url || data.country !== current.country || data.currency !== current.currency || data.locale !== current.locale) throw new Error('Product context did not match');
      const previousVariant = current.product.variants.find(variant => String(variant.id) === currentVariant);
      const sizePosition = current.options.findIndex(option => /^(size|taille|grandeur)$/i.test(option.name));
      const nextSizePosition = data.options.findIndex(option => /^(size|taille|grandeur)$/i.test(option.name));
      const variant = previousVariant && sizePosition >= 0 && nextSizePosition >= 0
        ? data.product.variants.find(item => item.options[nextSizePosition] === previousVariant.options[sizePosition])
        : null;
      if (variant && variant.id !== data.selectedVariant.id) {
        html = await requestHtml(url + '?variant=' + variant.id, false, context);
        next = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section--main-product');
      }
      const firstImage = next?.querySelector('.product-gallery__media img');
      if (firstImage) preloadImage(firstImage);
    } catch (_) {
      pdpPreloads.delete(key);
    }
  }

  document.addEventListener('pointerover', event => {
    const swatch = event.target.closest?.('.shopify-section--main-product [data-au-native-pdp] ' + SWATCH);
    if (!swatch || swatch.contains(event.relatedTarget) || event.pointerType === 'touch' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    preloadPdp(swatch);
  }, { passive: true });

  window.addEventListener('popstate', () => {
    ++pdpRequest;
    if (!document.querySelector('[data-au-native-pdp]')) return;
    if (location.pathname.includes('/products/')) switchPdp(location.href, false);
    else location.assign(location.href);
  });

  document.addEventListener('click', event => {
    const wishlistButton = event.target.closest?.('.wl-btn[data-wishlist]');
    if (wishlistButton && window.wishlistPlus) {
      const scope = wishlistButton.closest('quick-buy-modal, .shopify-section--main-product');
      const data = scope && productData(scope);
      if (data) {
        window.wishlistPlus.wlProduct = { ...data.product, selected_or_first_available_variant: data.selectedVariant };
        wishlistButton.setAttribute('data-proid', data.product.id);
        wishlistButton.setAttribute('data-varid', scope.querySelector('product-form input[name="id"]')?.value || data.selectedVariant.id);
      }
    }
    const more = event.target.closest?.('[data-au-native-more]');
    if (more) {
      const expanded = more.getAttribute('aria-expanded') !== 'true';
      more.setAttribute('aria-expanded', String(expanded));
      more.closest('[data-au-native-group-swatches]').toggleAttribute('data-expanded', expanded);
      more.textContent = expanded ? '−' : '+';
      more.setAttribute('aria-label', expanded ? more.dataset.lessLabel : more.dataset.moreLabel);
      return;
    }
    const swatch = event.target.closest?.(SWATCH);
    if (swatch) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const modal = swatch.closest('quick-buy-modal');
      const card = swatch.closest('product-card');
      if (modal) {
        event.preventDefault();
        switchModal(modal, swatch);
      } else if (card) {
        event.preventDefault();
        switchCard(card, swatch.href, true);
      } else if (swatch.closest('.shopify-section--main-product [data-au-native-pdp]')) {
        event.preventDefault();
        switchPdp(swatch.href);
      }
      return;
    }
    const button = event.target.closest?.('.boss-quick-size__button[data-au-variant-id], .boss-quick-shop__size[data-au-variant-id]');
    if (button) {
      const card = button.closest('product-card');
      if (card?.hasAttribute('data-au-switching')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      const input = button.closest('form')?.querySelector('input[name="id"]');
      if (input) input.value = button.dataset.auVariantId;
    }
  }, true);

  document.addEventListener('pointerover', event => {
    const swatch = event.target.closest?.(SWATCH);
    if (!swatch || swatch.contains(event.relatedTarget) || swatch.closest('quick-buy-modal')) return;
    const card = swatch.closest('product-card');
    if (!card || !matchMedia('(min-width: 700px) and (hover: hover) and (pointer: fine)').matches || event.pointerType === 'touch') return;
    const state = stateFor(card);
    if (state.pendingCommit) return;
    state.hover = swatch;
    switchCard(card, swatch.href, false);
  }, true);

  document.addEventListener('pointerout', event => {
    const swatch = event.target.closest?.(SWATCH);
    if (!swatch || swatch.contains(event.relatedTarget) || swatch.closest('quick-buy-modal')) return;
    const card = swatch.closest('product-card');
    if (!card) return;
    const state = states.get(card);
    if (!state?.hover || state.pendingCommit) return;
    const next = event.relatedTarget?.closest?.(SWATCH);
    if (next?.closest('product-card') === card) return;
    state.hover = null;
    switchCard(card, state.selected, false);
  }, true);

  document.addEventListener('pointerdown', event => {
    lastInput = 'pointer';
    pointerPosition = { x: event.clientX, y: event.clientY };
    const swatch = event.target.closest?.(SWATCH);
    const card = swatch?.closest('product-card');
    if (card && !swatch.closest('quick-buy-modal')) cardHtml(card, productUrl(swatch.href)).catch(() => {});
    else if (swatch?.closest('.shopify-section--main-product [data-au-native-pdp]')) preloadPdp(swatch);
  }, { capture: true, passive: true });

  document.addEventListener('pointermove', event => {
    pointerPosition = { x: event.clientX, y: event.clientY };
  }, { capture: true, passive: true });

  document.addEventListener('pointerover', event => {
    const card = event.target.closest?.('product-card');
    if (!card || card.contains(event.relatedTarget)) return;
    const position = dismissedAt.get(card);
    if (position && position.x === event.clientX && position.y === event.clientY) return;
    card.removeAttribute('data-au-quick-dismissed');
    dismissedAt.delete(card);
  }, { capture: true, passive: true });

  document.addEventListener('keydown', event => {
    lastInput = 'keyboard';
    const card = event.target.closest?.('product-card');
    card?.removeAttribute('data-au-quick-dismissed');
    if (card) dismissedAt.delete(card);
  }, true);

  document.addEventListener('focusin', event => {
    if (lastInput !== 'keyboard') return;
    const card = event.target.closest?.('product-card');
    card?.removeAttribute('data-au-quick-dismissed');
    if (card) dismissedAt.delete(card);
    const swatch = event.target.closest?.('.shopify-section--main-product [data-au-native-pdp] ' + SWATCH);
    if (swatch) preloadPdp(swatch);
  });

  document.addEventListener('dialog:before-show', event => {
    if (!event.target.matches?.('quick-buy-modal')) return;
    ownerFor(event.target)?.removeAttribute('data-au-quick-dismissed');
  }, true);

  document.addEventListener('dialog:after-show', event => {
    if (event.target.matches?.('quick-buy-modal')) refreshProductApps(event.target, productData(event.target));
  }, true);

  document.addEventListener('dialog:before-hide', event => {
    if (!event.target.matches?.('quick-buy-modal')) return;
    modalRequests.set(event.target, (modalRequests.get(event.target) || 0) + 1);
    event.target.removeAttribute('aria-busy');
  }, true);

  document.addEventListener('dialog:after-hide', event => {
    if (!event.target.matches?.('quick-buy-modal')) return;
    const owner = ownerFor(event.target);
    if (owner) {
      if (lastInput === 'pointer') {
        owner.dataset.auQuickDismissed = 'true';
        dismissedAt.set(owner, pointerPosition);
      } else {
        owner.removeAttribute('data-au-quick-dismissed');
        dismissedAt.delete(owner);
      }
    }
    const section = document.querySelector('.shopify-section--main-product');
    const data = section && productData(section);
    if (data && window.wishlistPlus) window.wishlistPlus.wlProduct = { ...data.product, selected_or_first_available_variant: data.selectedVariant };
  }, true);
})();

